"use client";

import { useCallback, useEffect, useRef } from "react";

/**
 * Garde le canvas de la carte à la taille de son conteneur.
 *
 * MapLibre dimensionne son `<canvas>` une seule fois, à l'initialisation, et ne
 * le redimensionne pas quand le conteneur change. Nos cartes vivent dans des
 * panneaux et des feuilles qui s'ouvrent en glissant : au moment où la carte se
 * monte, le conteneur n'a pas encore sa largeur finale. Résultat observé sur la
 * modale « Détails du partenaire » — la carte ne peignait que la moitié gauche
 * de son cadre, et le marqueur, positionné par rapport au conteneur, flottait
 * en dehors de la carte.
 *
 * C'est NOTRE observateur qui fait ce travail : celui de MapLibre est coupé
 * (`trackResize={false}` sur chaque `<Map>`) parce qu'il appelle `resize()`
 * après la destruction de la carte. Son rappel est throttlé à 50 ms, et son
 * garde-fou `!this._removed` est donc évalué trop tard : le démontage change la
 * mise en page, l'observateur se met en file, la carte est détruite, puis le
 * rappel s'exécute — `map.transform` n'existe plus et react-maplibre lève
 * « Cannot read properties of undefined (reading 'center') » depuis
 * `_onCameraEvent`. Reproduit, pile à l'appui, sur la carte de suivi livreur.
 *
 * Renvoie une paire de callbacks : `containerRef` sur l'élément qui porte la
 * carte, `onLoad` sur le composant `<Map>`.
 */

/** Le strict nécessaire : on n'a besoin que de `resize()`. */
type Resizable = { resize?: () => void };

export function useMapAutoResize() {
  const mapRef = useRef<Resizable | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const disposedRef = useRef(false);

  /**
   * Redimensionne, sauf si la carte est partie. Un rappel d'observateur peut se
   * déclencher au moment où le panneau se referme, et `resize()` émet alors des
   * événements caméra sur une carte à moitié démontée.
   *
   * La vivacité se lit sur NOTRE cycle de vie, pas sur une propriété de
   * MapLibre : `map.transform` — que suggère la pile de l'erreur — n'existe pas
   * sur l'objet exposé par cette version. Mesuré : `"transform" in map` est
   * faux sur une carte pourtant chargée et fonctionnelle, si bien qu'une garde
   * posée dessus n'aurait jamais laissé passer un seul redimensionnement.
   */
  const safeResize = useCallback(() => {
    if (disposedRef.current) return;
    const map = mapRef.current;
    if (!map?.resize) return;
    try {
      map.resize();
    } catch {
      // Un rappel d'observateur peut se déclencher pendant la destruction :
      // `resize()` émet alors des événements caméra sur une carte à moitié
      // démontée. Rien à sauver ici, mais rien à faire remonter non plus.
    }
  }, []);

  const onLoad = useCallback(
    (e: { target: unknown }) => {
      mapRef.current = e.target as Resizable;
      // Une passe immédiate : le conteneur peut avoir bougé entre le montage et
      // la fin du chargement du style.
      safeResize();
    },
    [safeResize],
  );

  /**
   * Ref-callback, et non `useRef` : le conteneur n'existe pas au premier rendu.
   * Tant que le style se charge, les cartes affichent un squelette, si bien
   * qu'un effet posé au montage trouvait `containerRef.current` à `null`,
   * repartait sans rien observer, et ne se rejouait jamais ensuite —
   * l'observateur n'était donc jamais installé. React appelle une ref-callback
   * avec le nœud au moment où il entre dans le DOM, et avec `null` quand il en
   * sort : le branchement suit la vraie vie de l'élément.
   */
  const containerRef = useCallback(
    (node: HTMLDivElement | null) => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      if (!node || typeof ResizeObserver === "undefined") {
        // Le conteneur part : plus rien ne doit toucher cette carte.
        disposedRef.current = true;
        mapRef.current = null;
        return;
      }
      disposedRef.current = false;
      const ro = new ResizeObserver(() => safeResize());
      ro.observe(node);
      observerRef.current = ro;
    },
    [safeResize],
  );

  useEffect(
    () => () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      disposedRef.current = true;
      mapRef.current = null;
    },
    [],
  );

  return { containerRef, onLoad };
}
